// Procedural texture library: the painters.
//
// Every surface in NSPIRE RECORDS is painted onto a 2D canvas at runtime — there
// are no artist assets. This half of the library holds the memo cache, the
// seeded RNG, the floor (colour + roughness), the wood and plaster surfaces, the
// zone dressing lookup, the handwriting text helpers, and the spine, price-card,
// poster and vinyl painters.
//
// Recovered from the production bundle.

import * as THREE from 'three';
import { ZONES } from './sections.js';
import { GENRES } from './catalogue.js';

// ---------------------------------------------------------------------------
// Cache, canvas and texture plumbing
// ---------------------------------------------------------------------------

const TEXTURE_CACHE = new Map;

function memoTexture(key, make) {
    TEXTURE_CACHE.has(key) || TEXTURE_CACHE.set(key, make());
    return TEXTURE_CACHE.get(key);
}

function makeCanvas(width, height) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    return [canvas, canvas.getContext("2d")];
}

function canvasTexture(canvas, {
    repeat = [1, 1],
    srgb = true,
    aniso = 16
} = {}) {
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(repeat[0], repeat[1]);
    texture.anisotropy = aniso;
    if (srgb) texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
}

// ---------------------------------------------------------------------------
// Seeded randomness. rnd() is the shared module-level stream; makeRng() hands
// out independent streams for textures that want their own repeatable noise.
// ---------------------------------------------------------------------------

let rndSeed = 1337;

function rnd() {
    rndSeed = (rndSeed * 1664525 + 1013904223) % 4294967296;
    return rndSeed / 4294967296;
}

function setRndSeed(seed) {
    rndSeed = seed;
}

function makeRng(seed) {
    return () => {
        seed = (seed * 1664525 + 1013904223) % 4294967296;
        return seed / 4294967296;
    };
}

// ---------------------------------------------------------------------------
// Floor
// ---------------------------------------------------------------------------

const PLANK_COUNT = 5;

// Board geometry shared by the floor colour map and the floor roughness map:
// row bounds, per-board segments with their tone multipliers, butt joints,
// gap width, optional knot, plus a scatter of wear patches.
function plankLayout() {
    return memoTexture("plankLayout", () => {
        const rng = makeRng(90210);
        const widths = [];
        let widthTotal = 0;
        for (let i = 0; i < PLANK_COUNT; i++) {
            const w = .7 + rng() * .66;
            widths.push(w);
            widthTotal += w;
        }
        const rowEdges = [0];
        for (let i = 0; i < PLANK_COUNT; i++) rowEdges.push(rowEdges[i] + widths[i] / widthTotal * 1024);
        rowEdges[PLANK_COUNT] = 1024;
        const boards = [];
        for (let i = 0; i < PLANK_COUNT; i++) {
            const jointCount = 1 + (rng() > .55 ? 1 : 0);
            const joints = [];
            for (let j = 0; j < jointCount; j++) joints.push((j + .18 + rng() * .64) / jointCount * 1024);
            const segs = [];
            const cuts = [0, ...joints, 1024];
            const toneBase = .8 + rng() * .34;
            const blueBase = .94 + rng() * .12;
            for (let j = 0; j < cuts.length - 1; j++) segs.push({
                x0: cuts[j],
                x1: cuts[j + 1],
                k: toneBase * (.955 + rng() * .09),
                b: blueBase * (.98 + rng() * .04),
                cup: .7 + rng() * .6
            });
            boards.push({
                y0: rowEdges[i],
                y1: rowEdges[i + 1],
                joints,
                segs,
                gap: 1.4 + rng() * 3.8,
                knot: rng() > .38 ? {
                    x: rng() * 1024,
                    t: .28 + rng() * .44,
                    s: .8 + rng() * .7
                } : null
            });
        }
        const wear = [];
        for (let i = 0; i < 54; i++) wear.push({
            x: rng() * 1024,
            y: rng() * 1024,
            rad: 55 + rng() * 175,
            pale: rng() > .46,
            a: rng()
        });
        return {
            ys: rowEdges,
            boards,
            wear
        };
    });
}

// The shop floor: stained boards, grain, knots, cupping, gaps, scuffs and wear.
function floorColorMap() {
    return memoTexture("floor", () => {
        const [canvas, ctx] = makeCanvas(1024, 1024);
        const layout = plankLayout();
        const {
            ys: rowEdges,
            boards
        } = layout;
        const DARK = "31,33,21";
        const BASE_RGB = [144, 124, 97];
        const rng = makeRng(7717);
        for (let boardIndex = 0; boardIndex < PLANK_COUNT; boardIndex++) {
            const board = boards[boardIndex];
            const top = board.y0;
            const height = board.y1 - board.y0;

            // Flat tone per segment.
            for (const seg of board.segs) {
                const r = BASE_RGB[0] * seg.k;
                const g = BASE_RGB[1] * seg.k;
                const b = BASE_RGB[2] * seg.k * seg.b;
                // Each board leans warm or cool a little, and drifts in value
                // along its length; the mean stays at the base tone.
                const lean = (rng() - .5) * .1;
                const drift = (rng() - .5) * .16;
                const tr = r * (1 + lean) | 0,
                    tg = g | 0,
                    tb = b * (1 - lean) | 0;
                ctx.fillStyle = `rgb(${tr},${tg},${tb})`;
                ctx.fillRect(seg.x0, top, seg.x1 - seg.x0 + 1, height + 1);
                const along = ctx.createLinearGradient(seg.x0, 0, seg.x1, 0);
                along.addColorStop(0, drift > 0 ? `rgba(${DARK},${drift})` : `rgba(226,196,150,${-drift*.5})`);
                along.addColorStop(1, drift > 0 ? `rgba(226,196,150,${drift*.5})` : `rgba(${DARK},${-drift})`);
                ctx.fillStyle = along;
                ctx.fillRect(seg.x0, top, seg.x1 - seg.x0 + 1, height + 1);
            }

            // Dark grain streaks running the length of the board.
            for (let i = 0; i < 16; i++) {
                const y = top + 2 + rng() * (height - 4);
                const len = 260 + rng() * 700;
                const x0 = rng() * 1024;
                ctx.strokeStyle = `rgba(${DARK},${.09+rng()*.16})`;
                ctx.lineWidth = 1.2 + rng() * 3.4;
                ctx.lineCap = "round";
                for (const shift of [-1024, 0, 1024]) {
                    ctx.beginPath();
                    ctx.moveTo(x0 + shift, y);
                    ctx.bezierCurveTo(x0 + shift + len * .3, y + (rng() - .5) * 3, x0 + shift + len * .7, y + (rng() - .5) * 3, x0 + shift + len, y + (rng() - .5) * 2);
                    ctx.stroke();
                }
                ctx.lineCap = "butt";
            }

            // Long wavy grain lines.
            for (let i = 0; i < 30; i++) {
                const y = top + rng() * height;
                const alpha = .06 + rng() * .15;
                const dark = rng() > .34;
                ctx.strokeStyle = dark ? `rgba(${DARK},${alpha})` : `rgba(198,172,128,${alpha*.55})`;
                ctx.lineWidth = 1 + rng() * 3;
                ctx.beginPath();
                ctx.moveTo(-40, y);
                for (let x = 0; x <= 1064; x += 133) ctx.lineTo(x, y + Math.sin((x + boardIndex * 400) * .006) * 3.5 + (rng() - .5) * 2.2);
                ctx.stroke();
            }

            // Short grain ticks.
            for (let i = 0; i < 900; i++) {
                const y = top + rng() * height;
                const x = rng() * 1024;
                const len = 60 + rng() * 380;
                const alpha = .03 + rng() * .09;
                ctx.strokeStyle = rng() > .5 ? `rgba(${DARK},${alpha})` : `rgba(214,190,146,${alpha*.6})`;
                ctx.lineWidth = .4 + rng() * 1;
                ctx.beginPath();
                ctx.moveTo(x, y);
                ctx.lineTo(x + len, y + (rng() - .5) * 2.2);
                ctx.stroke();
            }

            // Knot, drawn as nested ellipses.
            if (board.knot) {
                const knotX = board.knot.x;
                const knotY = top + height * board.knot.t;
                for (let ring = 12; ring > 0; ring--) {
                    ctx.strokeStyle = `rgba(${DARK},${.09+(12-ring)*.03})`;
                    ctx.lineWidth = 1.2;
                    ctx.beginPath();
                    ctx.ellipse(knotX, knotY, ring * 2.1 * board.knot.s, ring * .95 * board.knot.s, .2, 0, Math.PI * 2);
                    ctx.stroke();
                }
            }

            // Cupping: dark at the board edges, a highlight down the middle.
            for (const seg of board.segs) {
                const grad = ctx.createLinearGradient(0, top, 0, top + height);
                const edge = .3 * seg.cup;
                grad.addColorStop(0, `rgba(${DARK},${edge})`);
                grad.addColorStop(.15, `rgba(${DARK},0)`);
                grad.addColorStop(.5, `rgba(255,232,186,${.05*seg.cup})`);
                grad.addColorStop(.85, `rgba(${DARK},0)`);
                grad.addColorStop(1, `rgba(${DARK},${edge})`);
                ctx.fillStyle = grad;
                ctx.fillRect(seg.x0, top, seg.x1 - seg.x0 + 1, height + 1);
            }

            // The gap along the top edge of the board, wandering as it goes.
            let wobble = 0;
            let gapWidth = board.gap;
            for (let x = 0; x < 1024; x += 8) {
                wobble = wobble * .86 + (rng() - .5) * .7;
                gapWidth = gapWidth * .8 + board.gap * (.62 + rng() * .7) * .2;
                ctx.fillStyle = `rgba(${DARK},.96)`;
                ctx.fillRect(x, top + wobble - .4, 9, gapWidth);
                ctx.fillStyle = "rgba(22,19,11,.5)";
                ctx.fillRect(x, top + wobble + gapWidth, 9, 1.3 + rng() * 1.6);
                ctx.fillStyle = `rgba(255,228,180,${.1+rng()*.08})`;
                ctx.fillRect(x, top + wobble + gapWidth + 2.2, 9, 1.4);
            }
            // Grime packed along the seam, on the board above it.
            {
                const grime = ctx.createLinearGradient(0, top - 7, 0, top + 1);
                grime.addColorStop(0, `rgba(${DARK},0)`);
                grime.addColorStop(1, `rgba(${DARK},.3)`);
                ctx.fillStyle = grime;
                ctx.fillRect(0, top - 7, 1024, 8);
            }

            // Butt joints between boards in the same row.
            for (const jointX of board.joints) {
                ctx.fillStyle = `rgba(${DARK},.86)`;
                ctx.fillRect(jointX, top + 2, 1.6 + rng() * 1.4, height - 3);
                const grad = ctx.createLinearGradient(jointX - 14, 0, jointX + 16, 0);
                grad.addColorStop(0, `rgba(${DARK},0)`);
                grad.addColorStop(.46, `rgba(${DARK},.20)`);
                grad.addColorStop(.54, `rgba(${DARK},.20)`);
                grad.addColorStop(1, `rgba(${DARK},0)`);
                ctx.fillStyle = grad;
                ctx.fillRect(jointX - 14, top + 1, 30, height - 1);
                ctx.fillStyle = "rgba(236,212,168,.13)";
                ctx.fillRect(jointX + 2.6, top + 2, 1.4, height - 3);
                // Nail heads on both sides of the joint, each with a pale rim.
                for (const side of [-1, 1])
                    for (let nail = 0; nail < 2; nail++) {
                        const nx = jointX + side * (8 + rng() * 5),
                            ny = top + height * (.24 + nail * .5) + (rng() - .5) * 3;
                        ctx.fillStyle = `rgba(${DARK},.62)`;
                        ctx.beginPath();
                        ctx.ellipse(nx, ny, 2.9, 2.3, 0, 0, Math.PI * 2);
                        ctx.fill();
                        ctx.strokeStyle = "rgba(226,200,160,.32)";
                        ctx.lineWidth = .8;
                        ctx.beginPath();
                        ctx.ellipse(nx, ny + .5, 3.6, 2.9, 0, 0, Math.PI * 2);
                        ctx.stroke();
                    }
            }
        }

        // Soft blotches across the whole floor.
        for (let i = 0; i < 60; i++) {
            const x = rng() * 1024;
            const y = rng() * 1024;
            const rad = 14 + rng() * 44;
            const grad = ctx.createRadialGradient(x, y, 0, x, y, rad);
            const pale = rng() > .45;
            grad.addColorStop(0, pale ? `rgba(200,174,128,${.05+rng()*.08})` : `rgba(74,55,32,${.05+rng()*.09})`);
            grad.addColorStop(1, "rgba(0,0,0,0)");
            ctx.fillStyle = grad;
            ctx.fillRect(x - rad, y - rad, rad * 2, rad * 2);
        }

        // Scratches in random directions.
        for (let i = 0; i < 520; i++) {
            const dark = rng() > .42;
            ctx.strokeStyle = dark ? `rgba(${DARK},${.05+rng()*.14})` : `rgba(216,192,148,${.04+rng()*.1})`;
            ctx.lineWidth = .5 + rng() * 1.6;
            const x = rng() * 1024;
            const y = rng() * 1024;
            const len = 6 + rng() * 70;
            const angle = (rng() - .5) * .7;
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x + Math.cos(angle) * len, y + Math.sin(angle) * len);
            ctx.stroke();
        }

        // Dirt gathered along the row edges.
        for (let i = 0; i < PLANK_COUNT; i++) {
            const edgeY = rowEdges[i];
            for (let j = 0; j < 110; j++) {
                const x = rng() * 1024;
                const w = 16 + rng() * 110;
                const above = rng() > .5;
                const h = 3 + rng() * 13;
                const grad = ctx.createLinearGradient(0, above ? edgeY - h : edgeY, 0, above ? edgeY : edgeY + h);
                grad.addColorStop(above ? 0 : 1, `rgba(${DARK},0)`);
                grad.addColorStop(above ? 1 : 0, `rgba(${DARK},${.08+rng()*.15})`);
                ctx.fillStyle = grad;
                ctx.fillRect(x, above ? edgeY - h : edgeY, w, h);
            }
        }

        // Traffic wear patches.
        for (const patch of layout.wear) {
            const grad = ctx.createRadialGradient(patch.x, patch.y, patch.rad * .12, patch.x, patch.y, patch.rad);
            grad.addColorStop(0, patch.pale ? `rgba(210,184,140,${.05+patch.a*.08})` : `rgba(${DARK},${.05+patch.a*.1})`);
            grad.addColorStop(1, patch.pale ? "rgba(210,184,140,0)" : `rgba(${DARK},0)`);
            ctx.fillStyle = grad;
            ctx.fillRect(patch.x - patch.rad, patch.y - patch.rad, patch.rad * 2, patch.rad * 2);
        }

        // Long drag marks, each with a pale highlight underneath.
        for (let i = 0; i < 34; i++) {
            const y = rng() * 1024;
            const x = rng() * 1024;
            const len = 120 + rng() * 560;
            ctx.strokeStyle = `rgba(${DARK},${.12+rng()*.18})`;
            ctx.lineWidth = 1 + rng() * 2.5;
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x + len, y + (rng() - .5) * 4);
            ctx.stroke();
            ctx.strokeStyle = `rgba(210,186,142,${.06+rng()*.09})`;
            ctx.lineWidth = .8;
            ctx.beginPath();
            ctx.moveTo(x, y + 1.5);
            ctx.lineTo(x + len, y + 1.5 + (rng() - .5) * 4);
            ctx.stroke();
        }

        // Grit specks.
        for (let i = 0; i < 130; i++) {
            const x = rng() * 1024;
            const y = rng() * 1024;
            ctx.fillStyle = rng() > .58 ? `rgba(216,198,162,${.1+rng()*.2})` : `rgba(${DARK},${.1+rng()*.22})`;
            ctx.beginPath();
            ctx.ellipse(x, y, 1 + rng() * 3.5, .8 + rng() * 2, rng() * 3, 0, Math.PI * 2);
            ctx.fill();
        }

        return canvasTexture(canvas, {
            repeat: [3, 7]
        });
    });
}

// Roughness companion to floorColorMap, on the same plank layout.
function floorRoughnessMap() {
    return memoTexture("floorR", () => {
        const [canvas, ctx] = makeCanvas(1024, 1024);
        const layout = plankLayout();
        const rng = makeRng(4242);
        ctx.fillStyle = "#c8c8c8";
        ctx.fillRect(0, 0, 1024, 1024);
        for (const board of layout.boards) {
            for (const seg of board.segs) {
                const level = 202 + Math.round((seg.k / 1 - .9) / .34 * 44);
                ctx.fillStyle = `rgb(${level},${level},${level})`;
                ctx.fillRect(seg.x0, board.y0, seg.x1 - seg.x0 + 1, board.y1 - board.y0 + 1);
            }
            for (let i = 0; i < 700; i++) {
                const y = board.y0 + rng() * (board.y1 - board.y0);
                const x = rng() * 1024;
                ctx.strokeStyle = `rgba(255,255,255,${.05+rng()*.14})`;
                ctx.lineWidth = .4 + rng() * 1.2;
                ctx.beginPath();
                ctx.moveTo(x, y);
                ctx.lineTo(x + 50 + rng() * 340, y + (rng() - .5) * 2);
                ctx.stroke();
            }
            ctx.fillStyle = "rgba(255,255,255,0.95)";
            ctx.fillRect(0, board.y0 - 1, 1024, board.gap + 3);
            for (const jointX of board.joints) ctx.fillRect(jointX - 1, board.y0, board.gap + 3, board.y1 - board.y0);
        }
        for (const patch of layout.wear) {
            const grad = ctx.createRadialGradient(patch.x, patch.y, patch.rad * .12, patch.x, patch.y, patch.rad);
            grad.addColorStop(0, patch.pale ? `rgba(96,92,92,${.18+patch.a*.22})` : `rgba(255,255,255,${.16+patch.a*.22})`);
            grad.addColorStop(1, "rgba(0,0,0,0)");
            ctx.fillStyle = grad;
            ctx.fillRect(patch.x - patch.rad, patch.y - patch.rad, patch.rad * 2, patch.rad * 2);
        }
        for (let i = 0; i < 9e3; i++) {
            ctx.fillStyle = `rgba(${186+rng()*66},${186+rng()*66},${186+rng()*66},.30)`;
            ctx.fillRect(rng() * 1024, rng() * 1024, 2, 2);
        }
        return canvasTexture(canvas, {
            repeat: [3, 7],
            srgb: false
        });
    });
}

// ---------------------------------------------------------------------------
// Wood
// ---------------------------------------------------------------------------

// Shelving / crate / counter timber. hueShift slides the base colour; passing a
// repeat returns a cloned texture at that tiling.
function woodTexture(hueShift = 0, repeat = null) {
    // Callers pass a handful of near-identical shifts; they share the nearest
    // of four painted variants so the 1024px canvases stay at four.
    hueShift = WOOD_SHIFTS.reduce((best, v) => Math.abs(v - hueShift) < Math.abs(best - hueShift) ? v : best);
    if (repeat) {
        const key = `wood${hueShift}@${repeat[0].toFixed(3)}x${repeat[1].toFixed(3)}`;
        return memoTexture(key, () => {
            const clone = woodTexture(hueShift).clone();
            clone.repeat.set(repeat[0], repeat[1]);
            clone.needsUpdate = true;
            return clone;
        });
    }
    return memoTexture("wood" + hueShift, () => {
        const N = 1024;
        const [canvas, ctx] = makeCanvas(N, N);
        const base = 121 + hueShift;
        const baseRgb = [base + 34, base + 4, base - 40];
        const rng = makeRng(3301 + hueShift * 17);
        ctx.fillStyle = `rgb(${baseRgb[0]},${baseRgb[1]},${baseRgb[2]})`;
        ctx.fillRect(0, 0, N, N);

        // Large-scale tint: soft pale and dark clouds, wrapped so the tile
        // still repeats, so neighbouring panels do not carry the same stain.
        for (let i = 0; i < 9; i++) {
            const x = rng() * N;
            const y = rng() * N;
            const rad = 160 + rng() * 260;
            const dark = rng() > .5;
            for (const ox of [-N, 0, N])
                for (const oy of [-N, 0, N]) {
                    const grad = ctx.createRadialGradient(x + ox, y + oy, 0, x + ox, y + oy, rad);
                    grad.addColorStop(0, dark ? `rgba(60,38,20,${.07+rng()*.05})` : `rgba(226,192,150,${.06+rng()*.05})`);
                    grad.addColorStop(1, "rgba(0,0,0,0)");
                    ctx.fillStyle = grad;
                    ctx.fillRect(x + ox - rad, y + oy - rad, rad * 2, rad * 2);
                }
        }

        // Cathedral grain: arches of nested curves.
        for (let i = 0; i < 3; i++) {
            const centerX = rng() * N;
            const spread = 120 + rng() * 260;
            for (let ring = 0; ring < 9; ring++) {
                const offset = (ring - 4) * (spread / 9);
                ctx.strokeStyle = `rgba(84,60,38,${.04+rng()*.05})`;
                ctx.lineWidth = 3 + rng() * 7;
                ctx.beginPath();
                for (let y = -40; y <= N + 20; y += 26) {
                    const phase = y / N * Math.PI;
                    const x = centerX + offset + Math.sin(phase) * (spread * .55) * (1 - Math.abs(offset) / spread);
                    if (y === -40) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.stroke();
            }
        }

        // Fine straight grain across the board.
        for (let i = 0; i < 2600; i++) {
            const y = rng() * N;
            ctx.strokeStyle = rng() > .48 ? `rgba(44,31,20,${.03+rng()*.12})` : `rgba(198,172,138,${.02+rng()*.09})`;
            ctx.lineWidth = .5 + rng() * 1.5;
            ctx.beginPath();
            ctx.moveTo(-10, y);
            for (let x = 0; x <= N + 10; x += 64) ctx.lineTo(x, y + (rng() - .5) * 4.5);
            ctx.stroke();
        }

        // Knots.
        for (let i = 0; i < 2; i++) {
            const x = rng() * N;
            const y = rng() * N;
            const scale = 1.1 + rng() * 1.4;
            for (let ring = 13; ring > 0; ring--) {
                ctx.strokeStyle = `rgba(46,30,18,${.06+(13-ring)*.028})`;
                ctx.lineWidth = 1.6;
                ctx.beginPath();
                ctx.ellipse(x, y, ring * 1.5 * scale, ring * 2.6 * scale, .3 + rng() * .2, 0, Math.PI * 2);
                ctx.stroke();
            }
        }

        // Faint varnish sheen lines.
        for (let y = rng() * 17; y < N; y += 17) {
            ctx.strokeStyle = `rgba(255,238,208,${.025+rng()*.03})`;
            ctx.lineWidth = 1.4;
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(N, y + (rng() - .5) * 2.4);
            ctx.stroke();
        }

        // Blotches.
        for (let i = 0; i < 30; i++) {
            const x = rng() * N;
            const y = rng() * N;
            const rad = 28 + rng() * 124;
            const grad = ctx.createRadialGradient(x, y, 0, x, y, rad);
            const dark = rng() > .42;
            grad.addColorStop(0, dark ? `rgba(38,26,16,${.05+rng()*.1})` : `rgba(214,190,152,${.05+rng()*.09})`);
            grad.addColorStop(1, "rgba(0,0,0,0)");
            ctx.fillStyle = grad;
            ctx.fillRect(x - rad, y - rad, rad * 2, rad * 2);
        }

        // Scratches.
        for (let i = 0; i < 260; i++) {
            const x = rng() * N;
            const y = rng() * N;
            const len = 8 + rng() * 84;
            const angle = (rng() - .5) * 2.4;
            ctx.strokeStyle = rng() > .5 ? `rgba(40,27,17,${.05+rng()*.13})` : `rgba(222,200,164,${.05+rng()*.12})`;
            ctx.lineWidth = .7 + rng() * 1.5;
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x + Math.cos(angle) * len, y + Math.sin(angle) * len);
            ctx.stroke();
        }

        // Nail and tack holes.
        for (let i = 0; i < 26; i++) {
            const x = rng() * N;
            const y = rng() * N;
            ctx.fillStyle = `rgba(34,23,14,${.24+rng()*.3})`;
            ctx.fillRect(x, y, 2.4 + rng() * 2, 2.4 + rng() * 2);
            if (rng() > .55) ctx.fillRect(x + 8 + rng() * 6, y + (rng() - .5) * 4, 2.4, 2.4);
        }

        // Darken top and bottom edges.
        const edgeShade = ctx.createLinearGradient(0, 0, 0, N);
        edgeShade.addColorStop(0, "rgba(20,14,9,.30)");
        edgeShade.addColorStop(.2, "rgba(20,14,9,0)");
        edgeShade.addColorStop(.8, "rgba(20,14,9,0)");
        edgeShade.addColorStop(1, "rgba(20,14,9,.34)");
        ctx.fillStyle = edgeShade;
        ctx.fillRect(0, 0, N, N);

        return canvasTexture(canvas, {
            repeat: [1, 1]
        });
    });
}

const WOOD_SHIFTS = [-46, -30, -16, 4];

// ---------------------------------------------------------------------------
// Plaster wall
// ---------------------------------------------------------------------------

// Trowelled plaster: skim marks, patches, hairline cracks, a grubby band at
// hand height, pin holes, ghosts of removed frames and overall speckle.
function plasterTexture() {
    return memoTexture("plaster", () => {
        // Painted in 512-unit coordinates onto a 1024px canvas.
        const [canvas, ctx] = makeCanvas(1024, 1024);
        ctx.scale(2, 2);
        ctx.fillStyle = "#e4dccb";
        ctx.fillRect(0, 0, 512, 512);

        // Runs a stroke three times so it tiles across the horizontal seam.
        const wrapX = (x, draw) => {
            draw(x - 512);
            draw(x);
            draw(x + 512);
        };

        // Large-scale tint: warm and cool clouds a few hundred units across.
        for (let i = 0; i < 8; i++) {
            const y = rnd() * 512;
            const rad = 90 + rnd() * 130;
            const warm = rnd() > .5;
            wrapX(rnd() * 512, x => {
                const grad = ctx.createRadialGradient(x, y, 0, x, y, rad);
                grad.addColorStop(0, warm ? "rgba(214,190,150,.10)" : "rgba(176,178,170,.09)");
                grad.addColorStop(1, "rgba(0,0,0,0)");
                ctx.fillStyle = grad;
                ctx.fillRect(x - rad, y - rad, rad * 2, rad * 2);
            });
        }

        // Trowel sweeps.
        for (let i = 0; i < 42; i++) {
            const y = rnd() * 512;
            const len = 90 + rnd() * 260;
            const bow = (rnd() - .5) * 46;
            const pale = rnd() > .5;
            const alpha = .035 + rnd() * .05;
            wrapX(rnd() * 512, x => {
                ctx.strokeStyle = pale ? `rgba(255,251,240,${alpha})` : `rgba(150,138,116,${alpha*.85})`;
                ctx.lineWidth = 7 + rnd() * 20;
                ctx.lineCap = "round";
                ctx.beginPath();
                ctx.moveTo(x, y);
                ctx.quadraticCurveTo(x + len * .5, y + bow, x + len, y + bow * .3);
                ctx.stroke();
            });
        }

        // Soft-edged repair patches.
        for (let i = 0; i < 7; i++) {
            const y = 40 + rnd() * 392;
            const w = 34 + rnd() * 90;
            const h = 26 + rnd() * 70;
            wrapX(rnd() * 512, x => {
                drawBlurred(ctx, 512, 6, layer => {
                    layer.fillStyle = "rgba(216,208,192,.62)";
                    layer.beginPath();
                    layer.moveTo(x, y);
                    layer.lineTo(x + w, y + (rnd() - .5) * 8);
                    layer.lineTo(x + w * .94, y + h);
                    layer.lineTo(x - w * .04, y + h * .92);
                    layer.closePath();
                    layer.fill();
                }, .55);
            });
        }

        // Hairline cracks.
        for (let i = 0; i < 11; i++) {
            const startY = rnd() * 512;
            const segments = 4 + Math.floor(rnd() * 6);
            const dir = rnd() > .5 ? 1 : -1;
            wrapX(rnd() * 512, x => {
                ctx.strokeStyle = `rgba(122,108,88,${.14+rnd()*.16})`;
                ctx.lineWidth = .8 + rnd() * .7;
                ctx.beginPath();
                let cx = x;
                let cy = startY;
                ctx.moveTo(cx, cy);
                for (let s = 0; s < segments; s++) {
                    cx += dir * (6 + rnd() * 22);
                    cy += (rnd() - .3) * 26;
                    ctx.lineTo(cx, cy);
                }
                ctx.stroke();
            });
        }

        // Grubby band at hand height, with smears through it.
        {
            const band = ctx.createLinearGradient(0, 296.96, 0, 378.88);
            band.addColorStop(0, "rgba(128,116,96,0)");
            band.addColorStop(.5, "rgba(128,116,96,.15)");
            band.addColorStop(1, "rgba(128,116,96,0)");
            ctx.fillStyle = band;
            ctx.fillRect(0, 296.96, 512, 81.92);
            for (let i = 0; i < 60; i++) {
                const y = 296.96 + rnd() * 81.92;
                const w = 5 + rnd() * 34;
                wrapX(rnd() * 512, x => {
                    ctx.fillStyle = `rgba(52,44,36,${.05+rnd()*.13})`;
                    ctx.fillRect(x, y, w, .8 + rnd() * 2.2);
                });
            }
        }

        // Shadowy staining up near the ceiling.
        for (let i = 0; i < 14; i++) {
            const y = rnd() * 180;
            const rad = 20 + rnd() * 70;
            wrapX(rnd() * 512, x => {
                const grad = ctx.createRadialGradient(x, y, 0, x, y, rad);
                grad.addColorStop(0, "rgba(96,82,62,.16)");
                grad.addColorStop(1, "rgba(96,82,62,0)");
                ctx.fillStyle = grad;
                ctx.fillRect(x - rad, y - rad, rad * 2, rad * 2);
            });
        }

        // Pin and nail holes, each with a highlight below it.
        for (let i = 0; i < 26; i++) {
            const y = 30 + rnd() * 452;
            wrapX(rnd() * 512, x => {
                ctx.fillStyle = `rgba(60,50,38,${.22+rnd()*.3})`;
                ctx.beginPath();
                ctx.arc(x, y, .9 + rnd() * 1.4, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = "rgba(255,250,238,.14)";
                ctx.beginPath();
                ctx.arc(x + 1, y + 1.4, 1.6 + rnd(), 0, Math.PI * 2);
                ctx.fill();
            });
        }

        // Rectangular ghosts where something used to hang.
        for (let i = 0; i < 5; i++) {
            const y = 60 + rnd() * 312;
            const w = 40 + rnd() * 80;
            const h = 50 + rnd() * 90;
            wrapX(rnd() * 512, x => {
                ctx.strokeStyle = "rgba(178,166,144,.20)";
                ctx.lineWidth = 2;
                ctx.strokeRect(x, y, w, h);
                ctx.fillStyle = "rgba(250,246,236,.07)";
                ctx.fillRect(x, y, w, h);
            });
        }

        // Overall speckle.
        for (let i = 0; i < 5200; i++) {
            const pale = rnd();
            ctx.fillStyle = `rgba(${pale>.5?250:170},${pale>.5?246:160},${pale>.5?234:142},.08)`;
            ctx.fillRect(rnd() * 512, rnd() * 512, 1 + rnd() * 3, 1 + rnd() * 3);
        }

        // Fine pitting, one device pixel.
        for (let i = 0; i < 9000; i++) {
            const pale = rnd() > .5;
            ctx.fillStyle = pale ? "rgba(252,248,238,.10)" : "rgba(140,128,108,.10)";
            ctx.fillRect(rnd() * 512, rnd() * 512, .5, .5);
        }

        clampToPaper(ctx, 512, 230);
        return canvasTexture(canvas, {
            repeat: [3, 2]
        });
    });
}

// ---------------------------------------------------------------------------
// Small helpers and the zone dressing lookup
// ---------------------------------------------------------------------------

// Positive modulo of a floored index.
function wrapIndex(i, n) {
    return (Math.floor(i) % n + n) % n;
}

// Palette and saturation per zone, keyed by zone id.
const ZONE_DRESSING = Object.fromEntries(ZONES.map(zone => [zone.id, {
        colors: zone.palette.slice(),
        sat: zone.saturation
    }])),
    // Which zone's palette each record spine borrows.
    SPINE_ZONES = ["rock", "rock", "rock", "rock", "hiphop", "hiphop", "hiphop", "hiphop", "funk", "funk", "funk", "funk", "electronic", "electronic", "electronic", "electronic", "jazz", "jazz", "jazz", "jazz", "soul", "soul", "dub", "dub"],
    CANVAS_FILTER_SUPPORTED = (() => {
        try {
            return typeof document.createElement("canvas").getContext("2d").filter == "string";
        } catch {
            return false;
        }
    })();

// Draws `paint` onto an off-screen square canvas of `size`, then composites it
// back through a blur of `blurPx` at `alpha`.
function drawBlurred(ctx, size, blurPx, paint, alpha = 1) {
    const [layerCanvas, layerCtx] = makeCanvas(size, size);
    paint(layerCtx);
    ctx.save();
    ctx.globalAlpha = alpha;
    if (CANVAS_FILTER_SUPPORTED && blurPx > 0) ctx.filter = `blur(${blurPx}px)`;
    ctx.drawImage(layerCanvas, 0, 0);
    ctx.restore();
}

// Caps the canvas to a paper white by filling in "darken" mode.
function clampToPaper(ctx, width, white = 231, height = width) {
    ctx.save();
    ctx.globalCompositeOperation = "darken";
    ctx.fillStyle = `rgb(${white},${Math.round(white*.985)},${Math.round(white*.955)})`;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
}

// clampToPaper, but the canvas's existing alpha (torn edges) is put back
// afterwards via a destination-in of a pre-tint copy.
function clampToPaperKeepAlpha(canvas, ctx, width, white, height = width) {
    const [maskCanvas, maskCtx] = makeCanvas(width, height);
    maskCtx.drawImage(canvas, 0, 0);
    clampToPaper(ctx, width, white, height);
    ctx.save();
    ctx.globalCompositeOperation = "destination-in";
    ctx.drawImage(maskCanvas, 0, 0);
    ctx.restore();
}

// ---------------------------------------------------------------------------
// Record spines
// ---------------------------------------------------------------------------

const SPINE_COUNT = 24;

// One 64x512 sleeve spine. The base is a pale warm card so the per-instance
// tint decides the hue; printed bands, a typed title strip, a stripe, label
// dots and worn edges give each batch's strip its own character.
const SPINE_ARTISTS = ["THE VELVETS", "MARA LOWE", "BLUE ROOM", "KING TUBBY", "NINA & CO", "ORBITAL 9", "SUN QUARTET", "DJ HOLLOW", "THE FLINTS", "AFRO CUBE", "LEE SCRATCH", "MOON DRIVE", "CASSIA", "THE ROYALS", "DUB SYNDICATE", "HANK WILDE"],
    SPINE_TITLES = ["NIGHT SHIFT", "VOL. 2", "LIVE AT THE ROXY", "GREATEST HITS", "SESSIONS", "IN DUB", "SOUL SIDE", "HEAVY WEATHER", "MIDNIGHT TRAIN", "RARE GROOVE", "FIRST PRESSING", "OPEN ROAD", "THE B SIDES", "SUNDAY MIX"];

function spineTexture(index) {
    index = wrapIndex(index, SPINE_COUNT);
    return memoTexture("spine" + index, () => {
        const W = 64,
            H = 512;
        const [canvas, ctx] = makeCanvas(W, H);
        const colors = ZONE_DRESSING[SPINE_ZONES[index % SPINE_ZONES.length]].colors;
        const rng = makeRng(5100 + index * 131);
        const pick = list => list[Math.floor(rng() * list.length)];
        const tint = index * 37 % 50 - 25;
        ctx.fillStyle = `rgb(${232+tint*.4},${222+tint*.36},${200+tint*.32})`;
        ctx.fillRect(0, 0, W, H);
        for (let i = 0; i < 700; i++) {
            ctx.fillStyle = rng() > .5 ? `rgba(150,134,108,${rng()*.16})` : `rgba(255,250,236,${rng()*.2})`;
            ctx.fillRect(rng() * W, rng() * H, 1 + rng() * 5, 1);
        }
        // Printed band: a darker block with a stripe pair and label dot.
        const blockHeight = H * (.3 + rng() * .35);
        const blockTop = 24 + rng() * (H - blockHeight - 48);
        ctx.fillStyle = `rgba(40,32,26,${.28+rng()*.3})`;
        ctx.fillRect(0, blockTop, W, blockHeight);
        for (let i = 0; i < 2 + Math.floor(rng() * 2); i++) {
            ctx.fillStyle = pick(colors);
            ctx.globalAlpha = .75;
            const y = blockTop + rng() * blockHeight;
            ctx.fillRect(0, y, W, 4 + rng() * 10);
            ctx.globalAlpha = 1;
        }
        ctx.fillStyle = pick(colors);
        ctx.beginPath();
        ctx.arc(W / 2, 40 + rng() * 60, 10 + rng() * 4, 0, Math.PI * 2);
        ctx.fill();
        // Typed artist and title strips, reading along the spine.
        ctx.save();
        ctx.translate(W / 2, H / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.textBaseline = "middle";
        ctx.textAlign = "center";
        const artist = pick(SPINE_ARTISTS),
            title = pick(SPINE_TITLES);
        ctx.fillStyle = "rgba(255,252,242,.92)";
        ctx.fillRect(-180, -22, 360, 44);
        ctx.fillStyle = "rgba(24,18,14,.88)";
        ctx.font = `700 22px "Courier New", "DejaVu Sans Mono", monospace`;
        ctx.fillText(artist, 0, -8, 340);
        ctx.font = `500 15px "Courier New", "DejaVu Sans Mono", monospace`;
        ctx.fillText(title, 0, 11, 340);
        ctx.restore();
        // Worn top and bottom edges and side shading.
        ctx.fillStyle = `rgb(${244+tint*.3},${236+tint*.3},${216+tint*.3})`;
        ctx.fillRect(0, 0, W, 4);
        ctx.fillRect(0, H - 4, W, 4);
        for (let i = 0; i < 90; i++) {
            ctx.fillStyle = rng() > .5 ? "rgba(255,248,230,.45)" : "rgba(60,46,32,.3)";
            ctx.fillRect(rng() * W, rng() > .5 ? rng() * 8 : H - rng() * 8, 1 + rng() * 4, 1 + rng() * 2);
        }
        ctx.fillStyle = "rgba(0,0,0,.35)";
        ctx.fillRect(0, 0, 6, H);
        ctx.fillStyle = "rgba(255,246,226,.22)";
        ctx.fillRect(W - 5, 0, 5, H);
        ctx.fillStyle = "rgba(28,20,14,.26)";
        ctx.fillRect(0, 0, W, 9);
        ctx.fillRect(0, H - 11, W, 11);
        return canvasTexture(canvas);
    });
}

// ---------------------------------------------------------------------------
// Handwriting
// ---------------------------------------------------------------------------

const HAND_FONT = '"Bradley Hand","Segoe Print","Comic Sans MS","DejaVu Sans Condensed",sans-serif';

// Draws text in the handwriting stack, letter by letter, each glyph nudged and
// rotated a little and doubled at low alpha. Shrinks the size to fit maxWidth.
// Returns the size actually used.
function drawFittedText(ctx, text, x, y, maxWidth, size, color, opts = {}) {
    const {
        align = "center",
        weight = 700,
        jitter = 1,
        alpha = 1
    } = opts;
    const glyphs = String(text).toUpperCase().split("");
    let fontSize = size;
    const measure = px => {
        ctx.font = `${weight} ${px}px ${HAND_FONT}`;
        let total = 0;
        for (const glyph of glyphs) total += ctx.measureText(glyph).width + px * .045;
        return total;
    };
    let width = measure(fontSize);
    if (width > maxWidth) {
        fontSize *= maxWidth / width;
        width = measure(fontSize);
    }
    let penX = align === "left" ? x : x - width / 2;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = color;
    ctx.textBaseline = "alphabetic";
    for (const glyph of glyphs) {
        const advance = ctx.measureText(glyph).width + fontSize * .045;
        if (glyph !== " ") {
            ctx.save();
            ctx.translate(penX + advance / 2, y + (rnd() - .5) * fontSize * .05 * jitter);
            ctx.rotate((rnd() - .5) * .05 * jitter);
            ctx.font = `${weight+(rnd()>.7?100:0)} ${fontSize*(.97+rnd()*.06)}px ${HAND_FONT}`;
            ctx.textAlign = "center";
            ctx.fillText(glyph, 0, 0);
            ctx.globalAlpha = alpha * .3;
            ctx.fillText(glyph, (rnd() - .5) * 1.4, (rnd() - .5) * 1.2);
            ctx.restore();
        }
        penX += advance;
    }
    ctx.restore();
    return fontSize;
}

// A hand-drawn horizontal rule from x0 to x1, wobbling about y.
function drawHandRule(ctx, x0, x1, y, color, width = 2) {
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(x0, y);
    const steps = 6;
    for (let i = 1; i <= steps; i++) ctx.lineTo(x0 + (x1 - x0) * (i / steps), y + (rnd() - .5) * width * 1.6);
    ctx.stroke();
}

// Subgenre/genre label pairs. The result of this expression is discarded.
(() => {
    const pairs = [];
    for (const genre of GENRES)
        for (const sub of genre.subgenres || [])
            if (sub.id !== "all") pairs.push([sub.label, genre.label]);
    return pairs.length ? pairs : [
        ["NEW IN", "MISC"]
    ];
})();

// ---------------------------------------------------------------------------
// Price cards
// ---------------------------------------------------------------------------

// Shop-written cards salted into the genre cards below.
const EXTRA_CARD_LABELS = [
        ["NEW ARRIVALS", "JUST IN — NOT PRICED"],
        ["BARGAIN BIN", "50p EACH"],
        ["NO SLEEVE", "20p"],
        ["PLEASE ASK", "AT COUNTER"],
        ["SEVEN INCH", "A–Z"],
        ["TIMBO COLLECTION", "JUST IN"]
    ],
    // [heading, subheading] pairs: one per genre, with the extras spliced in
    // every third slot.
    CARD_LABELS = (() => {
        const labels = [];
        for (const genre of GENRES) {
            const subs = (genre.subgenres || []).filter(sub => sub.id !== "all");
            if (subs.length) labels.push([genre.label, subs[labels.length % subs.length].label]);
        }
        for (let i = 0; i < EXTRA_CARD_LABELS.length; i++) labels.splice(i * 3 + 1, 0, EXTRA_CARD_LABELS[i]);
        return labels;
    })(),
    CARD_COUNT = 16;

// Splits a label into at most two lines: on a slash if there is one, otherwise
// at the word break that most evenly balances the two halves.
function fitLabel(text) {
    const label = String(text).toUpperCase().trim();
    if (label.length <= 9) return [label];
    const slash = label.indexOf("/");
    if (slash > 0) return [label.slice(0, slash).trim(), label.slice(slash + 1).trim()];
    const words = label.split(/\s+/);
    if (words.length === 1) return [label];
    let bestSplit = 1,
        bestDelta = 1e9;
    for (let i = 1; i < words.length; i++) {
        const delta = Math.abs(words.slice(0, i).join(" ").length - words.slice(i).join(" ").length);
        if (delta < bestDelta) {
            bestDelta = delta;
            bestSplit = i;
        }
    }
    return [words.slice(0, bestSplit).join(" "), words.slice(bestSplit).join(" ")];
}

// Ages a sheet of paper in place: foxing specks, an optional cup ring, creases
// and a corner-to-corner fade.
function agePaper(ctx, width, height, rng, opts = {}) {
    const {
        fox = 1,
        ring = .35,
        crease = 1,
        fade = 1
    } = opts;
    const foxCount = Math.round(fox * (width * height) / 900);
    for (let i = 0; i < foxCount; i++) {
        const x = rng() * width;
        const y = rng() * height;
        const rad = .8 + rng() * 3.4;
        const grad = ctx.createRadialGradient(x, y, 0, x, y, rad);
        grad.addColorStop(0, `rgba(146,108,58,${.1+rng()*.22})`);
        grad.addColorStop(1, "rgba(146,108,58,0)");
        ctx.fillStyle = grad;
        ctx.fillRect(x - rad, y - rad, rad * 2, rad * 2);
    }
    if (rng() < ring) {
        const x = rng() * width;
        const y = rng() * height;
        const rad = Math.min(width, height) * (.18 + rng() * .16);
        ctx.strokeStyle = `rgba(128,88,44,${.13+rng()*.12})`;
        ctx.lineWidth = 1.6 + rng() * 2.4;
        ctx.beginPath();
        ctx.ellipse(x, y, rad, rad * (.9 + rng() * .2), rng(), 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = "rgba(128,88,44,.06)";
        ctx.lineWidth = rad * .8;
        ctx.beginPath();
        ctx.ellipse(x, y, rad * .6, rad * .55, 0, 0, Math.PI * 2);
        ctx.stroke();
    }
    for (let i = 0; i < crease * 2; i++) {
        const vertical = rng() > .5;
        const at = rng() * (vertical ? width : height);
        ctx.lineWidth = 1.2;
        for (const [strokeColor, offset] of [
                [`rgba(72,58,36,${.1+rng()*.1})`, 0],
                ["rgba(255,250,232,.16)", 1.4]
            ]) {
            ctx.strokeStyle = strokeColor;
            ctx.beginPath();
            for (let t = 0; t <= 1.001; t += .1) {
                const wobble = (rng() - .5) * (Math.min(width, height) * .05);
                const px = vertical ? at + wobble + offset : t * width;
                const py = vertical ? t * height : at + wobble + offset;
                if (t === 0) ctx.moveTo(px, py);
                else ctx.lineTo(px, py);
            }
            ctx.stroke();
        }
    }
    if (fade) {
        const grad = ctx.createLinearGradient(0, 0, width * .7, height);
        grad.addColorStop(0, "rgba(255,252,240,.07)");
        grad.addColorStop(.55, "rgba(255,252,240,0)");
        grad.addColorStop(1, "rgba(150,118,66,.20)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);
    }
}

// A 256x160 handwritten price / section card. Pass heading (and optionally
// subheading) to override the label taken from CARD_LABELS.
function cardTexture(index, heading = null, subheading = null) {
    index = wrapIndex(index, CARD_COUNT);
    const label = CARD_LABELS[index % CARD_LABELS.length] || ["NEW IN", null],
        topLine = heading || label[0],
        bottomLine = heading ? subheading : label[1];
    return memoTexture(heading ? `cardT:${topLine}|${bottomLine||""}` : "card" + index, () => {
        const [canvas, ctx] = makeCanvas(256, 160);
        ctx.fillStyle = "#eae0c6";
        ctx.fillRect(0, 0, 256, 160);
        const vignette = ctx.createRadialGradient(128, 80, 30, 128, 80, 160);
        vignette.addColorStop(0, "rgba(255,250,232,.45)");
        vignette.addColorStop(1, "rgba(196,168,112,.40)");
        ctx.fillStyle = vignette;
        ctx.fillRect(0, 0, 256, 160);

        // Card stock speckle and blotches.
        for (let i = 0; i < 900; i++) {
            ctx.fillStyle = rnd() > .5 ? `rgba(140,120,84,${rnd()*.12})` : `rgba(255,250,236,${rnd()*.12})`;
            ctx.fillRect(rnd() * 256, rnd() * 160, 1 + rnd() * 4, 1 + rnd() * 3);
        }
        for (let i = 0; i < 5; i++) {
            const x = rnd() * 256;
            const y = rnd() * 160;
            const rad = 12 + rnd() * 26;
            const grad = ctx.createRadialGradient(x, y, 0, x, y, rad);
            grad.addColorStop(0, `rgba(120,100,68,${.06+rnd()*.09})`);
            grad.addColorStop(1, "rgba(120,100,68,0)");
            ctx.fillStyle = grad;
            ctx.fillRect(x - rad, y - rad, rad * 2, rad * 2);
        }

        // Every third card gets a hand-drawn border.
        if (index % 3 === 0) {
            const borderColor = "rgba(56,50,40,.5)";
            drawHandRule(ctx, 12, 244, 13, borderColor, 2.5);
            drawHandRule(ctx, 12, 244, 147, borderColor, 2.5);
            drawHandRule(ctx, 13, 13, 147, borderColor, 2.5);
            ctx.strokeStyle = borderColor;
            ctx.lineWidth = 2.5;
            ctx.beginPath();
            ctx.moveTo(13, 13);
            ctx.lineTo(12, 147);
            ctx.moveTo(243, 13);
            ctx.lineTo(244, 147);
            ctx.stroke();
        }

        // The lettering, on a slight tilt about the card centre.
        const lines = fitLabel(topLine),
            inkColor = "rgb(28,24,38)";
        ctx.save();
        ctx.translate(128, 80);
        ctx.rotate((rnd() - .5) * .05);
        ctx.translate(-128, -80);
        if (lines.length === 1) {
            drawFittedText(ctx, lines[0], 128, bottomLine ? 74 : 92, 218, 54, inkColor, {
                jitter: 1.2
            });
            drawHandRule(ctx, 30, 226, bottomLine ? 86 : 104, "rgba(52,44,36,.55)", 3.2);
        } else {
            drawFittedText(ctx, lines[0], 128, 58, 220, 40, inkColor, {
                jitter: 1.2
            });
            drawFittedText(ctx, lines[1], 128, 100, 220, 40, inkColor, {
                jitter: 1.2
            });
            drawHandRule(ctx, 34, 222, 112, "rgba(52,44,36,.55)", 3);
        }
        if (bottomLine) drawFittedText(ctx, bottomLine, 128, lines.length === 1 ? 124 : 138, 206, 24, "rgb(122,38,30)", {
            jitter: 1.4,
            alpha: .94
        });
        ctx.restore();

        // Fixings: tape tabs on even cards, brass pins on odd ones.
        if (index % 2 === 0)
            for (let i = 0; i < 2 + index % 3; i++) {
                const x = i % 2 ? 228 : 26;
                const y = i < 2 ? 14 : 146;
                ctx.save();
                ctx.translate(x, y);
                ctx.rotate((rnd() - .5) * .7);
                ctx.fillStyle = "rgba(216,200,152,.5)";
                ctx.fillRect(-19, -10, 38, 20);
                ctx.fillStyle = "rgba(255,248,220,.22)";
                ctx.fillRect(-19, -10, 38, 4);
                ctx.fillStyle = "rgba(90,74,48,.18)";
                ctx.fillRect(-19, 9, 38, 2);
                ctx.restore();
            }
        else
            for (let i = 0; i < 1 + index % 2; i++) {
                const x = i ? 216 : 40;
                const y = 18 + rnd() * 8;
                ctx.fillStyle = "rgba(70,58,36,.35)";
                ctx.beginPath();
                ctx.arc(x + 1.5, y + 2, 6.5, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = "#a8842e";
                ctx.beginPath();
                ctx.arc(x, y, 6, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = "rgba(255,240,196,.75)";
                ctx.beginPath();
                ctx.arc(x - 1.8, y - 2, 2.4, 0, Math.PI * 2);
                ctx.fill();
            }

        // Tear the bottom and right edges out of the alpha, then draw the
        // torn lip back in.
        ctx.globalCompositeOperation = "destination-out";
        ctx.fillStyle = "#000";
        ctx.beginPath();
        ctx.moveTo(0, 160);
        for (let x = 0; x <= 256; x += 12) ctx.lineTo(x, 160 - rnd() * 10);
        ctx.lineTo(256, 160);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(256, 0);
        for (let y = 0; y <= 160; y += 12) ctx.lineTo(256 - rnd() * 7, y);
        ctx.lineTo(256, 160);
        ctx.fill();
        ctx.globalCompositeOperation = "source-over";
        ctx.strokeStyle = "rgba(255,250,232,.35)";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        for (let x = 0; x <= 256; x += 12) ctx.lineTo(x, 152 - rnd() * 6);
        ctx.stroke();

        agePaper(ctx, 256, 160, makeRng(881 + index * 37), {
            fox: 1,
            ring: .3,
            crease: 1
        });
        clampToPaperKeepAlpha(canvas, ctx, 256, 232, 160);
        return canvasTexture(canvas);
    });
}

// ---------------------------------------------------------------------------
// Gig posters
// ---------------------------------------------------------------------------

// [headline, support, venue, date, price] per poster design.
const GIG_POSTERS = [
    ["THE BLEACH", "DEAD CANAL", "THE HOPE & ANCHOR", "FRI 14 NOV", "£3 DOOR"],
    ["SLATE HOUSE", "PYLON WIVES", "THE 100 CLUB", "SAT 2 MAY", "ADM £4"],
    ["DUB SYNDICATE", "SOUND SYSTEM", "MOONSHOT HALL", "SUN 9 AUG", "ALL NITE"],
    ["COLD RIVET", "THE SPARES", "ROXY BASEMENT", "THU 30 JAN", "£2.50"],
    ["MARLA VOSS", "QUARTET", "RONNIE’S", "WED 11 JUN", "2 SETS"],
    ["BOOM BAP SOC.", "DJ TALL PAUL", "UNIT 7 WAREHOUSE", "FRI 21 MAR", "11 TIL LATE"],
    ["SEVEN SISTERS", "PLUS GUESTS", "THE GARAGE", "TUE 5 OCT", "TICKETS £5"],
    ["ACID TEST", "RESIDENTS", "THE VAULT", "SAT 18 FEB", "MEMBERS ONLY"]
];

// A 384x576 flyposted gig bill: block layout, hand lettering, sun fade, foxing,
// tape tabs and torn edges.
function posterTexture(index) {
    index = wrapIndex(index, POSTER_COUNT);
    return memoTexture("poster" + index, () => {
        const [canvas, ctx] = makeCanvas(384, 576);
        const design = index % 8;
        const copy = GIG_POSTERS[design];
        const STOCKS = ["#e9dfc0", "#efe7d4", "#e2d49c", "#e8ddc8", "#f0e8d6", "#ddd0ae", "#eadfbe", "#f1e6cd"];
        const INKS = [
            ["#b8261f", "#1d3f7a"],
            ["#1b1b1b", "#c2401d"],
            ["#8c1d2c", "#2c6152"],
            ["#20305e", "#c9761c"],
            ["#a51f28", "#1c1c1c"],
            ["#2a2118", "#b5372b"],
            ["#17253f", "#a8321f"],
            ["#1c1c1c", "#7a2f6a"]
        ];
        const stock = STOCKS[design];
        const inkA = INKS[design][0];
        const inkB = INKS[design][1];

        ctx.fillStyle = "#e8e2d2";
        ctx.fillRect(0, 0, 384, 576);
        ctx.save();
        ctx.translate(6, 6);
        ctx.beginPath();
        ctx.rect(0, 0, 372, 564);
        ctx.clip();
        ctx.fillStyle = stock;
        ctx.fillRect(0, 0, 372, 564);
        ctx.strokeStyle = inkA;
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.moveTo(22, 40);
        ctx.lineTo(350, 40);
        ctx.stroke();
        drawFittedText(ctx, "LIVE", 186, 84, 300, 34, inkB, {
            jitter: .5,
            alpha: .9
        });

        // Four block-layout variants behind the headline.
        if (design % 4 === 0) {
            ctx.fillStyle = inkA;
            ctx.fillRect(22, 104, 328, 190);
        } else if (design % 4 === 1) {
            ctx.fillStyle = inkB;
            ctx.beginPath();
            ctx.arc(186, 200, 118, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = inkA;
            ctx.fillRect(22, 128, 328, 148);
        } else if (design % 4 === 2) {
            ctx.fillStyle = inkB;
            for (let i = 0; i < 7; i++) ctx.fillRect(22, 108 + i * 28, 328, 13);
            ctx.fillStyle = inkA;
            ctx.fillRect(22, 128, 328, 148);
        } else {
            ctx.fillStyle = inkA;
            ctx.fillRect(22, 104, 328, 190);
            ctx.fillStyle = stock;
            ctx.fillRect(40, 122, 292, 154);
        }

        const headlineOnInk = design % 4 === 0 || design % 4 === 1 || design % 4 === 2;
        const headline = fitLabel(copy[0]);
        if (headline.length === 1) {
            drawFittedText(ctx, headline[0], 186, 220, 316, 96, headlineOnInk ? stock : inkA, {
                jitter: .5
            });
        } else {
            drawFittedText(ctx, headline[0], 186, 178, 316, 74, headlineOnInk ? stock : inkA, {
                jitter: .5
            });
            drawFittedText(ctx, headline[1], 186, 258, 316, 74, headlineOnInk ? stock : inkA, {
                jitter: .5
            });
        }
        drawFittedText(ctx, copy[1], 186, 340, 300, 44, inkA, {
            jitter: .6
        });
        ctx.strokeStyle = inkB;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(46, 360);
        ctx.lineTo(326, 360);
        ctx.stroke();
        drawFittedText(ctx, copy[2], 186, 408, 320, 40, inkB, {
            jitter: .6
        });
        drawFittedText(ctx, copy[3], 186, 462, 300, 44, inkA, {
            jitter: .6
        });
        drawFittedText(ctx, copy[4], 186, 512, 220, 32, inkB, {
            jitter: .7,
            alpha: .92
        });
        ctx.strokeStyle = inkA;
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.moveTo(22, 536);
        ctx.lineTo(350, 536);
        ctx.stroke();
        for (let i = 0; i < 900; i++) {
            ctx.fillStyle = `rgba(30,24,18,${rnd()*.1})`;
            ctx.fillRect(rnd() * 372, rnd() * 564, 1 + rnd() * 2, 1 + rnd() * 2);
        }
        ctx.restore();

        // Sun fade from a random direction.
        const fadeAngle = rnd() * Math.PI * 2;
        const fade = ctx.createLinearGradient(192 - Math.cos(fadeAngle) * 300, 288 - Math.sin(fadeAngle) * 380, 192 + Math.cos(fadeAngle) * 300, 288 + Math.sin(fadeAngle) * 380);
        fade.addColorStop(0, "rgba(214,196,150,.08)");
        fade.addColorStop(1, `rgba(214,196,150,${.16+rnd()*.18})`);
        ctx.fillStyle = fade;
        ctx.fillRect(0, 0, 384, 576);

        for (let i = 0; i < 40 + rnd() * 60; i++) {
            ctx.fillStyle = "rgba(40,30,20,.10)";
            ctx.fillRect(rnd() * 384, rnd() * 576, 1 + rnd() * 3, 1 + rnd() * 3);
        }

        // Occasional damp stain.
        if (rnd() < .22) {
            const x = rnd() * 384;
            const y = rnd() * 576;
            const rad = 90 + rnd() * 70;
            const grad = ctx.createRadialGradient(x, y, rad * .2, x, y, rad);
            grad.addColorStop(0, "rgba(92,74,48,.22)");
            grad.addColorStop(1, "rgba(92,74,48,0)");
            ctx.fillStyle = grad;
            ctx.fillRect(x - rad, y - rad, rad * 2, rad * 2);
        }

        // Tape tabs at the corners.
        for (let i = 0; i < 1 + Math.floor(rnd() * 3); i++) {
            const x = rnd() > .5 ? 40 : 344;
            const y = rnd() > .5 ? 28 : 548;
            ctx.save();
            ctx.translate(x, y);
            ctx.rotate((rnd() - .5) * 1.2);
            ctx.fillStyle = "rgba(226,214,178,.5)";
            ctx.fillRect(-30, -12, 60, 24);
            ctx.restore();
        }

        // Ragged bites taken out of the edges.
        ctx.globalCompositeOperation = "destination-out";
        ctx.fillStyle = "#000";
        for (let i = 0; i < 2 + Math.floor(rnd() * 3); i++) {
            const span = 26 + rnd() * 60;
            const edge = Math.floor(rnd() * 4);
            let x = edge === 0 ? rnd() * 384 : edge === 1 ? 384 : edge === 2 ? rnd() * 384 : 0,
                y = edge === 0 ? 0 : edge === 1 ? rnd() * 576 : edge === 2 ? 576 : rnd() * 576;
            ctx.beginPath();
            ctx.moveTo(x, y);
            for (let step = 0; step < 4; step++) {
                x += (rnd() - .5) * span;
                y += (rnd() - .5) * span;
                ctx.lineTo(x, y);
            }
            ctx.closePath();
            ctx.fill();
        }
        ctx.globalCompositeOperation = "source-over";

        clampToPaperKeepAlpha(canvas, ctx, 384, 230, 576);
        return canvasTexture(canvas);
    });
}
const POSTER_COUNT = 24;

// ---------------------------------------------------------------------------
// Vinyl
// ---------------------------------------------------------------------------

// A record: grooves, a red label with two title bars, and the spindle hole.
function vinylTexture() {
    return memoTexture("vinyl", () => {
        const [canvas, ctx] = makeCanvas(512, 512);
        ctx.fillStyle = "#0a0a0b";
        ctx.fillRect(0, 0, 512, 512);
        for (let rad = 246; rad > 96; rad -= 2) {
            ctx.strokeStyle = `rgba(${44+rnd()*26},${44+rnd()*26},${48+rnd()*26},.5)`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.arc(256, 256, rad, 0, Math.PI * 2);
            ctx.stroke();
        }
        ctx.fillStyle = "#c8462f";
        ctx.beginPath();
        ctx.arc(256, 256, 92, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "rgba(245,235,215,.9)";
        ctx.fillRect(196, 232, 120, 9);
        ctx.fillRect(214, 250, 84, 6);
        ctx.fillStyle = "#0a0a0b";
        ctx.beginPath();
        ctx.arc(256, 256, 10, 0, Math.PI * 2);
        ctx.fill();
        return canvasTexture(canvas);
    });
}

// ---------------------------------------------------------------------------
// Genre labels
// ---------------------------------------------------------------------------

const GENRE_LABELS = GENRES.map(genre => ({
    id: genre.id,
    label: String(genre.label).toUpperCase(),
    subgenres: (genre.subgenres || []).filter(sub => sub.id !== "all").map(sub => ({
        id: sub.id,
        label: String(sub.label).toUpperCase()
    }))
}));
// The result of this expression is discarded.
Object.fromEntries(GENRE_LABELS.map(genre => [genre.id, genre]));

export {
    memoTexture,
    makeCanvas,
    canvasTexture,
    rnd,
    rndSeed,
    setRndSeed,
    makeRng,
    PLANK_COUNT,
    plankLayout,
    floorColorMap,
    floorRoughnessMap,
    woodTexture,
    plasterTexture,
    wrapIndex,
    ZONE_DRESSING,
    SPINE_ZONES,
    CANVAS_FILTER_SUPPORTED,
    drawBlurred,
    clampToPaper,
    clampToPaperKeepAlpha,
    SPINE_COUNT,
    spineTexture,
    HAND_FONT,
    drawFittedText,
    drawHandRule,
    EXTRA_CARD_LABELS,
    CARD_LABELS,
    CARD_COUNT,
    fitLabel,
    agePaper,
    cardTexture,
    GIG_POSTERS,
    posterTexture,
    POSTER_COUNT,
    vinylTexture,
    GENRE_LABELS,
};
